import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  AppBar,
  Toolbar,
  Alert,
  Step,
  StepLabel,
  Stepper,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { styled, useMediaQuery } from '@mui/system';
import { useAuth } from '../contexts/AuthContext';
import LogoutButton from '../components/LogoutButton';
import { theme } from '../theme/theme';
import { createRegion } from '../services/regions.services';
import { States } from '../types/auth.types';

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  phone: Yup.string()
    .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
    .required('Phone number is required'),
  address: Yup.string().required('Address is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zipCode: Yup.string()
    .matches(/^[0-9]{5}$/, 'Zip code must be 5 digits')
    .required('Zip code is required'),
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6),
  },
}));

const StyledForm = styled(Form)({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
});

const NewRegionRegistration: React.FC = () => {
  //Use useAuth to get authStatus and completeRegistration function
  const { authStatus, completeRegistration } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  //Steps for the stepper panel
  const steps = ['Director Info', 'Region Info'];
  const [activeStep, setActiveStep] = useState(0);
  //States for region info
  const [regionName, setRegionName] = useState('');
  const [regionLocation, setRegionLocation] = useState('');
  const [regionState, setRegionState] = useState('');
  //State for registration error messages
  const [registrationError, setRegistrationError] = useState<string | null>(
    null
  );

  //Variables for the form
  const initialValues = {
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
  };

  //When the user completes their & region registration and signs up
  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    //Set a variable for the region ID
    let newRegionId: string | null = null;
    try {
      //Create a new region with the given region name, location, and state
      const regionId = await createRegion(
        regionName,
        regionLocation,
        authStatus.user?.uid || '', //directorId aka this user's ID
        regionState
      );
      //Set the returned region ID of the newly created region docucment to the variable
      newRegionId = regionId;
      //Check if the region ID is valid
      if (!newRegionId) {
        throw new Error(
          'Failed to retrieve the ID of the newly created region.'
        );
      }
    } catch (error) {
      console.error('Error creating region:', error);
      return;
    }

    //Now create the user document
    try {
      //Reset the registration error state
      setRegistrationError(null);

      //Check if the user is authenticated
      if (!authStatus.user) {
        throw new Error('User not authenticated');
      }

      //Create an object with the registration data (including the region ID we got from creating the new region)
      const registrationData = {
        ...values,
        userType: 'admin', //change user type to admin now region registration is complete
        regionId: newRegionId,
      };
      //Submit registration data to complete the registration process (After Registration is completed, AccessGuard will handle navigation to admin dashboard)
      await completeRegistration(registrationData);
    } catch (error) {
      console.error('Registration error:', error);
      setRegistrationError(
        'An error occurred during registration. Please try again.'
      );
      setSubmitting(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div">
            Region Registration
          </Typography>
          <LogoutButton />
        </Toolbar>
      </AppBar>
      <Box sx={{ maxWidth: 600, margin: 'auto', mt: 4, px: 2 }}>
        <StyledPaper elevation={3}>
          <Typography variant="h4" gutterBottom align="center" color="primary">
            Complete Your Region Registration
          </Typography>

          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ mb: 4 }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {registrationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {registrationError}
            </Alert>
          )}

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting }) => (
              <StyledForm>
                {activeStep === 0 && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="firstName"
                          label="First Name"
                          error={touched.firstName && Boolean(errors.firstName)}
                          helperText={touched.firstName && errors.firstName}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="lastName"
                          label="Last Name"
                          error={touched.lastName && Boolean(errors.lastName)}
                          helperText={touched.lastName && errors.lastName}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="phone"
                          label="Phone Number"
                          error={touched.phone && Boolean(errors.phone)}
                          helperText={touched.phone && errors.phone}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="address"
                          label="Address"
                          error={touched.address && Boolean(errors.address)}
                          helperText={touched.address && errors.address}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="city"
                          label="City"
                          error={touched.city && Boolean(errors.city)}
                          helperText={touched.city && errors.city}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="state"
                          label="State"
                          error={touched.state && Boolean(errors.state)}
                          helperText={touched.state && errors.state}
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Field
                          as={TextField}
                          fullWidth
                          name="zipCode"
                          label="Zip Code"
                          error={touched.zipCode && Boolean(errors.zipCode)}
                          helperText={touched.zipCode && errors.zipCode}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {activeStep === 1 && (
                  <>
                    <TextField
                      label="Region Name"
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      value={regionName}
                      onChange={(e) => setRegionName(e.target.value)}
                    />
                    <TextField
                      label="Location"
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      value={regionLocation}
                      onChange={(e) => setRegionLocation(e.target.value)}
                    />
                    <FormControl fullWidth variant="outlined" margin="dense">
                      <InputLabel id="state-select-label">State</InputLabel>
                      <Select
                        labelId="state-select-label"
                        value={regionState}
                        onChange={(e) => setRegionState(e.target.value)}
                        label="State"
                      >
                        {Array.isArray(States) &&
                          States.map((state) => (
                            <MenuItem key={state} value={state}>
                              {state}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep((prev) => prev - 1)}
                  >
                    Back
                  </Button>
                  {activeStep === steps.length - 1 ? (
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Complete Registration'}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveStep((prev) => prev + 1);
                      }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
              </StyledForm>
            )}
          </Formik>
        </StyledPaper>
      </Box>
    </>
  );
};

export default NewRegionRegistration;
